import { Select, Button, Chip } from 'common/components';
import { PlusIcon, MinusIcon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { WorkflowConfigFormData } from 'pages/WorkflowBuilder/AgenticWorkflowBuilder';
import { TransitionRule } from 'services/api/WorkflowServiceAPI';
import { CallTypeCategories } from 'types/call.type';

const variableOptions = [
    { value: 'disposition', label: 'Disposition' },
    { value: 'outbound_status', label: 'Outbound status' },
];

const conditionOptions = [
    { value: 'last_contains', label: 'Last contains' },
    { value: 'last_not_contains', label: "Last doesn't contain" },
    { value: 'history_contains', label: 'History contains' },
    { value: 'history_not_contains', label: "History doesn't contain" },
];

const logicOptions = [
    { value: 'and', label: 'AND' },
    { value: 'or', label: 'OR' },
];

/**
 * Dispositions come from the qualification pipeline rather than configuration,
 * so this list is fixed. A rule naming anything else would save and never fire.
 */
const dispositionOptions = ['QUALIFY', 'UNQUALIFIED', 'UNDETERMINED', 'CALLBACK', 'NOT_INTERESTED'].map((value) => ({
    value,
    label: value,
}));

/**
 * Only statuses a finished call can hold. Transitions are evaluated at
 * end-of-call post-processing, so a rule on a transient status such as RINGING
 * could never match.
 */
const outboundStatusOptions = Object.entries(CallTypeCategories)
    .filter(([, category]) => category === 'Terminal')
    .map(([status]) => ({ value: status, label: status }));

const emptyRule: TransitionRule = { variable: 'disposition', condition: 'last_contains', value: '' };

/**
 * Defines the rules that move a party between statuses after a call.
 *
 * Transitions are evaluated in order and the first match wins, so the list is
 * numbered and reorderable. With more than one transition the order decides the
 * outcome, and it would otherwise be invisible.
 */
export const PartyStatusTransitionsTab = () => {
    const {
        control,
        watch,
        setValue,
        getValues,
        formState: { errors },
    } = useFormContext<WorkflowConfigFormData>();

    const { fields, append, remove, swap } = useFieldArray({ control, name: 'statusTransitions' });
    const statuses = watch('partyStatuses') ?? [];
    const transitions = watch('statusTransitions') ?? [];

    const targetOptions = statuses.filter((s) => s.name?.trim()).map((s) => ({ value: s.name, label: s.name }));

    if (targetOptions.length === 0) {
        return (
            <p className='text-gray-700 pt-xl'>
                Define party statuses first. A transition has to move the party to one of them.
            </p>
        );
    }

    return (
        <div className='flex flex-col gap-lg pt-xl'>
            <p className='text-gray-700'>
                Transitions run at the end of a call, in the order listed. The first one whose conditions all hold is
                applied, and the rest are skipped.
            </p>

            <div className='flex justify-end'>
                <Button
                    variant='secondary'
                    icon={PlusIcon}
                    onClick={() => append({ rules: [{ ...emptyRule }], logic: 'and', target: '' })}
                >
                    Create New
                </Button>
            </div>

            {fields.length === 0 && <p className='text-gray-700'>No transitions created yet</p>}

            <div className='flex flex-col gap-lg'>
                {fields.map((field, index) => {
                    const rules = transitions[index]?.rules ?? [];
                    return (
                        <div key={field.id} className='flex flex-col gap-sm' id={`statusTransitions-${index}`}>
                            <div className='flex items-center gap-sm'>
                                <Chip variant='filled' color='info' size='sm'>
                                    {index + 1}
                                </Chip>
                                <Button
                                    variant='secondary'
                                    icon={ArrowUpIcon}
                                    aria-label={`Move transition ${index + 1} earlier`}
                                    disabled={index === 0}
                                    onClick={() => swap(index, index - 1)}
                                />
                                <Button
                                    variant='secondary'
                                    icon={ArrowDownIcon}
                                    aria-label={`Move transition ${index + 1} later`}
                                    disabled={index === fields.length - 1}
                                    onClick={() => swap(index, index + 1)}
                                />
                                <Button
                                    variant='secondary'
                                    icon={MinusIcon}
                                    aria-label={`Remove transition ${index + 1}`}
                                    onClick={() => remove(index)}
                                />
                            </div>

                            {rules.map((rule, ruleIndex) => (
                                <div key={ruleIndex} className='flex gap-sm'>
                                    {ruleIndex > 0 && (
                                        <Controller
                                            name={`statusTransitions.${index}.logic`}
                                            control={control}
                                            render={({ field: logicField }) => (
                                                <Select
                                                    label='Join with'
                                                    options={logicOptions}
                                                    value={logicField.value ?? 'and'}
                                                    onChange={(e) => logicField.onChange(e.target.value)}
                                                />
                                            )}
                                        />
                                    )}
                                    <Controller
                                        name={`statusTransitions.${index}.rules.${ruleIndex}.variable`}
                                        control={control}
                                        render={({ field: variableField }) => (
                                            <Select
                                                label='Variable'
                                                options={variableOptions}
                                                value={variableField.value}
                                                onChange={(e) => {
                                                    variableField.onChange(e.target.value);
                                                    // The value vocabulary depends on the variable, so a
                                                    // leftover value from the other list would silently
                                                    // never match.
                                                    setValue(`statusTransitions.${index}.rules.${ruleIndex}.value`, '');
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`statusTransitions.${index}.rules.${ruleIndex}.condition`}
                                        control={control}
                                        render={({ field: conditionField }) => (
                                            <Select
                                                label='Condition'
                                                options={conditionOptions}
                                                value={conditionField.value}
                                                onChange={(e) => conditionField.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`statusTransitions.${index}.rules.${ruleIndex}.value`}
                                        control={control}
                                        rules={{ required: 'Value is required' }}
                                        render={({ field: valueField, fieldState }) => (
                                            <Select
                                                label='Value'
                                                options={
                                                    rule.variable === 'disposition'
                                                        ? dispositionOptions
                                                        : outboundStatusOptions
                                                }
                                                value={valueField.value}
                                                onChange={(e) => valueField.onChange(e.target.value)}
                                                error={!!fieldState.error}
                                                errorText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                    {rules.length > 1 && (
                                        <Button
                                            variant='secondary'
                                            icon={MinusIcon}
                                            aria-label={`Remove condition ${ruleIndex + 1} of transition ${index + 1}`}
                                            onClick={() =>
                                                setValue(
                                                    `statusTransitions.${index}.rules`,
                                                    rules.filter((_, i) => i !== ruleIndex)
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            ))}

                            <div className='flex gap-sm'>
                                <Button
                                    variant='secondary'
                                    icon={PlusIcon}
                                    onClick={() =>
                                        setValue(`statusTransitions.${index}.rules`, [
                                            ...(getValues(`statusTransitions.${index}.rules`) ?? []),
                                            { ...emptyRule },
                                        ])
                                    }
                                >
                                    Add condition
                                </Button>
                                <Controller
                                    name={`statusTransitions.${index}.target`}
                                    control={control}
                                    rules={{ required: 'Target status is required' }}
                                    render={({ field: targetField }) => (
                                        <Select
                                            label='Target Party Status'
                                            options={targetOptions}
                                            value={targetField.value}
                                            onChange={(e) => targetField.onChange(e.target.value)}
                                            error={!!errors.statusTransitions?.[index]?.target}
                                            errorText={errors.statusTransitions?.[index]?.target?.message}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
